/**
 * AddDeviceModal — Modal dialog for registering a new device on a license.
 *
 * Validates device code as UUIDv4 format, optional device name.
 */

import { useState, useEffect } from 'react';
import { X, Plus, Loader2 } from 'lucide-react';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function AddDeviceModal({ isOpen, onClose, onSubmit, isLoading, initialDeviceCode = '' }) {
    const [deviceCode, setDeviceCode] = useState('');
    const [deviceName, setDeviceName] = useState('');
    const [error, setError] = useState(null);

    // Reset form state when modal opens — pre-fill if initialDeviceCode provided
    useEffect(() => {
        if (isOpen) {
            setDeviceCode(initialDeviceCode || '');
            setDeviceName('');
            setError(null);
        }
    }, [isOpen, initialDeviceCode]);

    if (!isOpen) return null;

    function handleSubmit(e) {
        e.preventDefault();
        const trimmedCode = deviceCode.trim();

        if (!trimmedCode) {
            setError('Device code is required.');
            return;
        }
        if (!UUID_RE.test(trimmedCode)) {
            setError('Invalid format. Paste the device code from your extension (UUID format).');
            return;
        }

        setError(null);
        onSubmit(trimmedCode, deviceName.trim() || undefined);
    }

    function handleClose() {
        setDeviceCode('');
        setDeviceName('');
        setError(null);
        onClose();
    }

    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/40 dark:bg-black/60"
                onClick={handleClose}
                aria-hidden="true"
            />

            {/* Modal */}
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="add-device-title"
                className="relative w-full max-w-md bg-white dark:bg-zinc-900 rounded-xl shadow-2xl border border-zinc-200 dark:border-zinc-700 animate-slide-up"
            >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200 dark:border-zinc-700">
                    <h2 id="add-device-title" className="text-base font-semibold">
                        Add Device
                    </h2>
                    <button
                        type="button"
                        onClick={handleClose}
                        className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800"
                        aria-label="Close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Body */}
                <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
                    <div>
                        <label
                            htmlFor="device-code"
                            className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5"
                        >
                            Device Code <span className="text-red-500">*</span>
                        </label>
                        <input
                            id="device-code"
                            type="text"
                            value={deviceCode}
                            onChange={(e) => setDeviceCode(e.target.value)}
                            placeholder="e.g. a1b2c3d4-e5f6-4a7b-8c9d-0e1f2a3b4c5d"
                            autoFocus
                            autoComplete="off"
                            spellCheck="false"
                            className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 font-mono"
                        />
                        <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
                            Find this in your extension settings → License → Device Code.
                        </p>
                    </div>

                    <div>
                        <label
                            htmlFor="device-name"
                            className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5"
                        >
                            Device Name <span className="text-zinc-400">(optional)</span>
                        </label>
                        <input
                            id="device-name"
                            type="text"
                            value={deviceName}
                            onChange={(e) => setDeviceName(e.target.value)}
                            placeholder='e.g. "Work laptop", "Home PC"'
                            maxLength={50}
                            className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                        />
                    </div>

                    {error && (
                        <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                            {error}
                        </p>
                    )}

                    {/* Footer actions */}
                    <div className="flex justify-end gap-3 pt-2">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-800 rounded-lg hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {isLoading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Plus className="w-4 h-4" />
                            )}
                            Add Device
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
